/**
 * Public production-parity routine + LiveCoach flow.
 *
 * The data is intentionally a fixed example, but the interaction is not a
 * mock: this page renders the same routine components, launches LiveCoach from
 * the same "Guide me through it" CTA, and uses the same completion, close,
 * feedback, camera, MediaPipe, and access-gating behavior as /routine/:id.
 * The only persistence difference is snapshots: guests can exercise the real
 * shutter/compositing path, but the resulting image stays in memory for this
 * demo session and is never uploaded.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import Header from "../components/Header";
import LiveCoach from "../components/LiveCoach";
import MiniFeedbackToast from "../components/MiniFeedbackToast";
import RoutineActiveStep from "../components/RoutineActiveStep";
import RoutineDisclosure from "../components/RoutineDisclosure";
import RoutineStepsSidebar from "../components/RoutineStepsSidebar";
import { track } from "../lib/analytics";

const DEMO_ROUTINE = {
  id: "live-coach-demo",
  intro: "Let's build a soft, lifted look together — one placement at a time.",
  skin_observations:
    "This example routine uses the same guidance and mirror controls as a personalized TruBeauté routine.",
  goal: "everyday",
  experience: "beginner",
  beard_shadow: true,
  closing: "Look at you — balanced, lifted, and entirely yourself.\nxo, Trouvé",
  steps: [
    {
      order: 1,
      title: "Color correct under-eye",
      category: "color_corrector",
      what: "Tap a peach or salmon color corrector lightly along the inner under-eye.",
      where: "Inner corner of each under-eye, in a small triangle pointing toward the cheek.",
      why: "Neutralizes blue and grey shadows before concealer goes on.",
      tip: "Press, don't rub. Less is more.",
    },
    {
      order: 2,
      title: "Soften the jaw with contour",
      category: "contour",
      what: "Sweep a cool-toned contour just under the jawline.",
      where: "From the ear hollow, following the jaw curve toward the chin.",
      why: "Contour emphasizes the shape you want to celebrate.",
      tip: "Blend upward — you want a shadow, not a stripe.",
    },
    {
      order: 3,
      title: "Lift the apples with blush",
      category: "blush",
      what: "Smile, then sweep blush onto the highest point of your cheek.",
      where: "On the apple of each cheek, lifted slightly toward the temple.",
      why: "A high, warm flush brings the whole look to life.",
      tip: "Trouvé says: a little higher than feels right.",
    },
    {
      order: 4,
      title: "Emphasize the eyes",
      category: "eyeshadow",
      what: "Press a warm shimmer across the lid, then deepen the outer V.",
      where: "Across the lid and outer crease, stopping before the inner corner.",
      why: "A softly defined eye balances the lifted cheek and jaw placement.",
      tip: "Look down to apply, then look up to check.",
    },
  ],
};

const DEMO_PREFERENCES = {
  coverage: "medium",
  texture: "cream",
  eye_finish: "shimmer",
  ethics: ["cruelty_free"],
  skip_categories: [],
};

export default function LiveCoachDemo() {
  const { t } = useTranslation();
  const [activeStep, setActiveStep] = useState(0);
  const [completed, setCompleted] = useState(new Set());
  const [mirrorOpen, setMirrorOpen] = useState(false);
  const [showMiniFeedback, setShowMiniFeedback] = useState(false);
  const mirrorSessionSnaps = useRef(0);
  const miniFeedbackTimer = useRef(null);
  const stepCardRef = useRef(null);
  const currentStep = DEMO_ROUTINE.steps[activeStep];

  useEffect(
    () => () => {
      if (miniFeedbackTimer.current) clearTimeout(miniFeedbackTimer.current);
    },
    [],
  );

  const toggleComplete = useCallback((idx) => {
    setCompleted((previous) => {
      const next = new Set(previous);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  }, []);

  const selectStep = useCallback((idx) => {
    setActiveStep(idx);
  }, []);

  const next = useCallback(() => {
    setCompleted((previous) => new Set([...previous, activeStep]));
    if (activeStep >= DEMO_ROUTINE.steps.length - 1) return;
    setActiveStep((idx) => idx + 1);
    requestAnimationFrame(() => {
      stepCardRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    });
  }, [activeStep]);

  const prev = useCallback(() => {
    setActiveStep((idx) => Math.max(0, idx - 1));
  }, []);

  const openMirror = useCallback(
    (mode) => {
      track("learn_with_trouve_started", {
        at_step: activeStep,
        mode,
        surface: "live_coach_demo",
      });
      mirrorSessionSnaps.current = 0;
      setShowMiniFeedback(false);
      setMirrorOpen(true);
    },
    [activeStep],
  );

  const closeMirror = useCallback(() => {
    track("learn_with_trouve_closed_early", {
      at_step: activeStep,
      surface: "live_coach_demo",
    });
    setMirrorOpen(false);

    if (mirrorSessionSnaps.current === 0) {
      if (miniFeedbackTimer.current) clearTimeout(miniFeedbackTimer.current);
      miniFeedbackTimer.current = setTimeout(
        () => setShowMiniFeedback(true),
        3000,
      );
    }
  }, [activeStep]);

  // LiveCoach still performs the real camera-frame composition. Returning a
  // metadata-only record here keeps the demo's snapshot lifecycle identical
  // without uploading a guest's face or retaining the base64 frame.
  const saveDemoSnapshot = useCallback(async (payload) => ({
    id: `demo-${Date.now()}`,
    routine_id: DEMO_ROUTINE.id,
    step_idx: payload.step_idx,
    step_title: payload.step_title,
    created_at: new Date().toISOString(),
    demo_only: true,
  }), []);

  return (
    <div className="relative min-h-screen trubeaute-gradient">
      <Header />

      <section
        className="pt-24 sm:pt-32 pb-20 px-4 sm:px-6"
        data-testid="live-coach-demo-routine"
      >
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-10 text-center max-w-2xl mx-auto"
          >
            <span className="inline-block text-xs uppercase tracking-[0.25em] text-[#9B6A6C] mb-3">
              {t("routine.kicker")}
            </span>
            <h1 className="font-serif italic text-2xl sm:text-4xl lg:text-5xl text-[#2C2123] leading-tight mb-4">
              {DEMO_ROUTINE.intro}
            </h1>
            <p className="text-[14px] sm:text-base text-[#5C4B51] leading-relaxed">
              {DEMO_ROUTINE.skin_observations}
            </p>
            <div className="flex items-center justify-center gap-2 mt-5 flex-wrap">
              <span className="text-xs bg-[#F2E8E9] text-[#5D4050] rounded-full px-3 py-1">
                {t(`goalLabels.${DEMO_ROUTINE.goal}`)}
              </span>
              <span className="text-xs bg-[#F5EFEB] text-[#5C4B51] rounded-full px-3 py-1">
                {t(`expLabels.${DEMO_ROUTINE.experience}`)}
              </span>
              <span className="text-xs bg-[#FAF6F2] text-[#5C4B51] rounded-full px-3 py-1">
                {t("routine.totalTime", {
                  minutes: Math.max(5, DEMO_ROUTINE.steps.length * 2),
                })}
              </span>
              <span className="text-xs bg-[#F8EEEE] text-[#5D4050] rounded-full px-3 py-1">
                {t("routine.includesColorCorrection")}
              </span>
            </div>
          </motion.div>

          <div className="bg-white border border-[#EADDD7] rounded-2xl p-4 mb-6 soft-shadow">
            <div className="flex items-center justify-between text-xs uppercase tracking-[0.2em] text-[#8A7D82] mb-3">
              <span>{t("routine.yourProgress")}</span>
              <span data-testid="routine-progress-text">
                {completed.size} / {DEMO_ROUTINE.steps.length}
              </span>
            </div>
            <div className="h-2 w-full bg-[#F5EFEB] rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-[#D5A8A4] to-[#9B6A6C]"
                initial={{ width: 0 }}
                animate={{
                  width: `${(completed.size / DEMO_ROUTINE.steps.length) * 100}%`,
                }}
                transition={{ duration: 0.6 }}
              />
            </div>
          </div>

          <div className="mb-6">
            <RoutineDisclosure
              routine={DEMO_ROUTINE}
              preferences={DEMO_PREFERENCES}
            />
          </div>

          <div className="grid lg:grid-cols-[280px_1fr] gap-6">
            <RoutineStepsSidebar
              steps={DEMO_ROUTINE.steps}
              activeStep={activeStep}
              completed={completed}
              onSelect={selectStep}
            />
            <RoutineActiveStep
              routine={DEMO_ROUTINE}
              activeStep={activeStep}
              currentStep={currentStep}
              completed={completed}
              onToggleComplete={toggleComplete}
              stepProducts={[]}
              arsenalForStep={[]}
              arsenalByBrandName={new Set()}
              arsenalAdding={new Set()}
              onAddProductToArsenal={() => {}}
              cvExplainById={{}}
              cvSkinReasonById={{}}
              cvShadesById={{}}
              skinSignals={null}
              cvRecommendations={{}}
              onPrev={prev}
              onNext={next}
              onOpenMirror={openMirror}
              user={null}
              sessionPhoto={null}
              cardRef={stepCardRef}
            />
          </div>

          <div className="mt-10 text-center max-w-2xl mx-auto">
            <p className="font-serif italic text-xl text-[#5D4050] leading-relaxed">
              {DEMO_ROUTINE.closing.split("\n")[0]}
            </p>
            <p className="mt-2 font-serif italic text-sm text-[#9B6A6C]">
              {DEMO_ROUTINE.closing.split("\n")[1]}
            </p>
          </div>
        </div>
      </section>

      <AnimatePresence>
        {mirrorOpen && (
          <LiveCoach
            steps={DEMO_ROUTINE.steps}
            initialStepIdx={activeStep}
            routineId={DEMO_ROUTINE.id}
            closing={DEMO_ROUTINE.closing}
            onClose={closeMirror}
            onStepDone={(idx) =>
              setCompleted((previous) => new Set([...previous, idx]))
            }
            onSnapshotSaved={() => {
              mirrorSessionSnaps.current += 1;
            }}
            saveSnapshotOverride={saveDemoSnapshot}
            snapshotSavedMessage="Saved for this demo session only."
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showMiniFeedback && (
          <MiniFeedbackToast
            context="learn_with_trouve_closed_early"
            onClose={() => setShowMiniFeedback(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
